<template>
  <div class="sci-view-container">
    <!-- Top Glassmorphic Navigation -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button class="sci-btn-back" @click="home">
          <i class="fas fa-arrow-left"></i>
          <span>Gestionale</span>
        </button>
        <div class="sci-title-group">
          <h1 class="sci-title">
            <i class="fas fa-barcode me-2 text-primary"></i>
            Mappatura Barcode Prodotti
          </h1>
          <p class="sci-subtitle">Collega i codici a barre fisici alle schede prodotto per il magazzino e la cassa</p>
        </div>
      </div>
      <div class="sci-header-right">
        <button 
          class="sci-btn" 
          :class="scan ? 'sci-btn-danger' : 'sci-btn-primary'"
          @click="toggleScanner"
        >
          <i class="fas" :class="scan ? 'fa-video-slash' : 'fa-camera'"></i>
          <span>{{ scan ? 'Spegni Fotocamera' : 'Attiva Scanner Fotocamera' }}</span>
        </button>
      </div>
    </header>

    <!-- Feedback Toast Notification -->
    <transition name="toast-fade">
      <div v-if="alert" class="sci-floating-toast">
        <i class="fas fa-check-circle toast-icon"></i>
        <span>Prodotto mappato e salvato con successo!</span>
      </div>
    </transition>

    <!-- Main Spatial Bento Grid -->
    <div class="sci-bento-grid">
      <!-- Left Column: Camera Scanner Studio -->
      <section class="sci-card scanner-panel">
        <div class="sci-card-header">
          <div class="sci-card-title-group">
            <h2 class="sci-card-title">
              <i class="fas fa-expand-arrows-alt me-2 text-primary"></i>
              Mirino Fotocamera
            </h2>
            <span class="sci-badge" :class="scan ? 'sci-badge-success' : 'sci-badge-neutral'">
              {{ scan ? 'Fotocamera Attiva' : 'Standby' }}
            </span>
          </div>
          <span class="sci-caption">Inquadra il codice a barre all'interno dell'area</span>
        </div>

        <div class="scanner-viewport-wrapper">
          <div v-if="scan" class="camera-stream-box">
            <StreamBarcodeReader
              @decode="(a,b,c) => onDecode(a,b,c)"
              @loaded="onLoaded"
              @close="onClose"
            ></StreamBarcodeReader>
            <!-- High-tech Viewfinder Overlay -->
            <div class="scanner-reticle">
              <div class="reticle-corner top-left"></div>
              <div class="reticle-corner top-right"></div>
              <div class="reticle-corner bottom-left"></div>
              <div class="reticle-corner bottom-right"></div>
              <div class="scan-laser-beam"></div>
            </div>
          </div>
          <div v-else class="scanner-idle-state" @click="toggleScanner">
            <div class="idle-icon-halo">
              <i class="fas fa-camera"></i>
            </div>
            <h3 class="idle-title">Scanner non attivo</h3>
            <p class="idle-desc">Fai clic sul pulsante sottostante o in alto per attivare la telecamera del dispositivo.</p>
            <button class="sci-btn sci-btn-primary mt-3" @click.stop="toggleScanner">
              <i class="fas fa-play me-2"></i>
              Avvia Scanner
            </button>
          </div>
        </div>

        <div class="scanner-helper-tips">
          <div class="tip-item">
            <i class="fas fa-lightbulb text-warning"></i>
            <span>Assicurati che vi sia luce sufficiente e che il codice non sia riflesso o piegato.</span>
          </div>
        </div>
      </section>

      <!-- Right Column: Product Mapping Details Card -->
      <section class="sci-card form-panel">
        <div class="sci-card-header">
          <div class="sci-card-title-group">
            <h2 class="sci-card-title">
              <i class="fas fa-tag me-2 text-primary"></i>
              Dati Prodotto da Mappare
            </h2>
            <span v-if="code" class="sci-badge sci-badge-info">Codice Rilevato</span>
          </div>
          <span class="sci-caption">Verifica il codice letto o digitalo manualmente</span>
        </div>

        <div class="sci-form-body">
          <!-- Barcode Field -->
          <div class="sci-form-group">
            <label class="sci-label" for="barcode-input">
              <span>Codice a Barre (Barcode / EAN)</span>
              <span class="sci-label-hint">Rilevato automaticamente o manuale</span>
            </label>
            <div class="sci-input-icon-group">
              <i class="fas fa-barcode input-icon"></i>
              <input 
                id="barcode-input"
                type="text" 
                v-model="code" 
                placeholder="Es. 8001234567890" 
                class="sci-input font-monospace text-uppercase"
                autocomplete="off"
              />
              <button 
                v-if="code" 
                class="clear-input-btn" 
                title="Pulisci codice"
                @click="code = ''"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>

          <!-- Description Field -->
          <div class="sci-form-group">
            <label class="sci-label" for="desc-input">
              <span>Descrizione / Denominazione Prodotto</span>
              <span class="sci-label-hint">Nome che comparirà nel gestionale</span>
            </label>
            <div class="sci-input-icon-group">
              <i class="fas fa-box-open input-icon"></i>
              <input 
                id="desc-input"
                type="text" 
                v-model="descrizione" 
                placeholder="Es. Acqua Naturale 50cl, Birra Bionda 33cl..." 
                class="sci-input"
              />
              <button 
                v-if="descrizione" 
                class="clear-input-btn" 
                title="Pulisci descrizione"
                @click="descrizione = ''"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>

          <!-- Preview & Action Box -->
          <div class="mapping-summary-box" v-if="code || descrizione">
            <div class="summary-row">
              <span class="summary-label">Barcode:</span>
              <span class="summary-value font-monospace">{{ code || '—' }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Descrizione:</span>
              <span class="summary-value">{{ descrizione || '—' }}</span>
            </div>
          </div>

          <div class="sci-form-actions">
            <button 
              class="sci-btn sci-btn-primary w-100 btn-save-action" 
              :disabled="!code || !descrizione"
              @click="saveProduct"
            >
              <i class="fas fa-save me-2"></i>
              Salva e Associa Prodotto
            </button>
          </div>

          <div class="mt-4 pt-3 border-top">
            <router-link to="/tabprod" class="sci-link-subtle">
              <i class="fas fa-table me-2"></i>
              Visualizza tutti i prodotti già mappati ({{ prodotti.length }})
            </router-link>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { StreamBarcodeReader } from "vue-barcode-reader";
import { store } from '../store/store.js'

export default {
  name: 'MappaturaP',
  components: {
    StreamBarcodeReader,
  },
  data() {
    return {
      scan: false,
      text: '',
      id: null,
      descrizione: '',
      code: '',
      prodotti: [],
      alert: false,
      store
    }
  },
  methods: {
    home() {
      this.$router.push({ name: "Gestionale" })
    },
    toggleScanner() {
      this.scan = !this.scan;
    },
    onDecode(a) {
      console.log('Decoded barcode:', a);
      this.text = a;
      this.code = a;
      if (this.id) clearTimeout(this.id);
      this.id = setTimeout(() => {
        if (this.text === a) {
          this.text = "";
        }
      }, 5000);
    },
    onLoaded() {
      console.log("Scanner stream loaded");
    },
    onClose() {
      this.scan = false;
    },
    saveProduct() {
      if (!this.code || !this.descrizione) return;
      this.axios.post(this.store.path + `mappaP`, {
        code: this.code,
        descrizio: this.descrizione,
        headers: {
          authorization: `Bearer ${JSON.parse(localStorage.token)}`
        }
      }).then((response) => {
        if (response.data) {
          this.alert = true;
          this.code = '';
          this.descrizione = '';
          this.displayProduct();
          setTimeout(() => {
            this.alert = false;
          }, 3000);
        } else {
          console.warn('Non è stato possibile mappare questo prodotto');
        }
      }).catch(err => {
        console.error('Errore durante il salvataggio prodotto:', err);
      });
    },
    displayProduct() {
      this.axios.post(this.store.path + 'displayProduct', {
        headers: {
          authorization: `Bearer ${JSON.parse(localStorage.token)}`
        }
      }).then((response) => {
        if (response.data && response.data.messaggio) {
          this.prodotti = response.data.messaggio;
        }
      }).catch(err => {
        console.error('Errore durante il caricamento prodotti:', err);
      });
    },
  },
  beforeMount() {
    this.displayProduct();
  },
  beforeUnmount() {
    if (this.id) clearTimeout(this.id);
  }
}
</script>

<style scoped>
.sci-view-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--sci-bg, #f8fafc);
  color: var(--sci-text-main, #0f172a);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding-bottom: 40px;
}

/* Glassmorphic Header */
.sci-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 28px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.04);
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  color: #334155;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.sci-btn-back:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
  color: #0f172a;
  transform: translateX(-2px);
}

.sci-title-group {
  display: flex;
  flex-direction: column;
}

.sci-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
}

.sci-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.85rem;
  color: #64748b;
}

/* Floating Toast */
.sci-floating-toast {
  position: fixed;
  top: 85px;
  right: 28px;
  z-index: 999;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  background: #065f46;
  color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 25px -5px rgba(6, 95, 70, 0.4);
  font-weight: 600;
  font-size: 0.95rem;
}

.toast-icon {
  font-size: 1.25rem;
  color: #34d399;
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translateY(-15px) scale(0.95);
}

/* Bento Grid */
.sci-bento-grid {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 24px;
  max-width: 1400px;
  margin: 24px auto;
  padding: 0 24px;
  width: 100%;
}

@media (max-width: 960px) {
  .sci-bento-grid {
    grid-template-columns: 1fr;
  }
}

/* Bento Cards */
.sci-card {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sci-card-header {
  padding: 20px 24px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafbfc;
}

.sci-card-title-group {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.sci-card-title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
}

.sci-caption {
  font-size: 0.85rem;
  color: #64748b;
}

/* Scanner Viewport */
.scanner-viewport-wrapper {
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #0f172a;
  min-height: 420px;
  position: relative;
  overflow: hidden;
}

.camera-stream-box {
  width: 100%;
  max-width: 580px;
  border-radius: 14px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 0 30px rgba(0,0,0,0.5);
}

.camera-stream-box :deep(video) {
  width: 100% !important;
  height: 100% !important;
  max-height: 400px;
  object-fit: cover;
  border-radius: 14px;
  display: block;
}

/* Futuristic Reticle */
.scanner-reticle {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 260px;
  height: 160px;
  pointer-events: none;
}

.reticle-corner {
  position: absolute;
  width: 24px;
  height: 24px;
  border-color: #10b981;
  border-style: solid;
}

.reticle-corner.top-left {
  top: 0;
  left: 0;
  border-width: 4px 0 0 4px;
  border-top-left-radius: 6px;
}

.reticle-corner.top-right {
  top: 0;
  right: 0;
  border-width: 4px 4px 0 0;
  border-top-right-radius: 6px;
}

.reticle-corner.bottom-left {
  bottom: 0;
  left: 0;
  border-width: 0 0 4px 4px;
  border-bottom-left-radius: 6px;
}

.reticle-corner.bottom-right {
  bottom: 0;
  right: 0;
  border-width: 0 4px 4px 0;
  border-bottom-right-radius: 6px;
}

.scan-laser-beam {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, transparent, #10b981, transparent);
  box-shadow: 0 0 12px #10b981;
  animation: laserScan 2s infinite ease-in-out alternate;
}

@keyframes laserScan {
  0% { top: 0%; opacity: 0.6; }
  100% { top: 100%; opacity: 1; }
}

/* Idle State */
.scanner-idle-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;
  cursor: pointer;
}

.idle-icon-halo {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #e2e8f0;
  margin-bottom: 16px;
  transition: transform 0.2s ease;
}

.scanner-idle-state:hover .idle-icon-halo {
  transform: scale(1.08);
  background: rgba(16, 185, 129, 0.2);
  color: #10b981;
}

.idle-title {
  margin: 0 0 8px 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #f1f5f9;
}

.idle-desc {
  margin: 0;
  font-size: 0.9rem;
  max-width: 380px;
  color: #94a3b8;
  line-height: 1.4;
}

.scanner-helper-tips {
  padding: 16px 20px;
  background: #f8fafc;
  border-top: 1px solid #f1f5f9;
}

.tip-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.85rem;
  color: #475569;
}

/* Right Panel: Form Styling */
.sci-form-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.sci-form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sci-label {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 0.9rem;
  font-weight: 600;
  color: #334155;
}

.sci-label-hint {
  font-size: 0.75rem;
  font-weight: normal;
  color: #94a3b8;
}

.sci-input-icon-group {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 14px;
  color: #94a3b8;
  font-size: 1rem;
  pointer-events: none;
}

.sci-input {
  width: 100%;
  min-height: 46px;
  padding: 10px 40px 10px 42px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.95rem;
  color: #0f172a;
  background: #ffffff;
  transition: all 0.2s ease;
}

.sci-input:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
}

.font-monospace {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace !important;
  letter-spacing: 0.05em;
}

.clear-input-btn {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
}

.clear-input-btn:hover {
  color: #ef4444;
}

/* Mapping summary box */
.mapping-summary-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.88rem;
}

.summary-label {
  color: #64748b;
  font-weight: 500;
}

.summary-value {
  color: #0f172a;
  font-weight: 600;
  text-align: right;
  max-width: 65%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Buttons and Badges */
.sci-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 20px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.sci-btn-primary {
  background: #10b981;
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.25);
}

.sci-btn-primary:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.35);
}

.sci-btn-danger {
  background: #ef4444;
  color: #ffffff;
}

.sci-btn-danger:hover {
  background: #dc2626;
}

.sci-btn:disabled {
  background: #cbd5e1;
  color: #94a3b8;
  cursor: not-allowed;
  box-shadow: none;
}

.btn-save-action {
  min-height: 48px;
  font-size: 1rem;
}

.sci-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.sci-badge-success {
  background: #ecfdf5;
  color: #059669;
}

.sci-badge-neutral {
  background: #f1f5f9;
  color: #64748b;
}

.sci-badge-info {
  background: #eff6ff;
  color: #2563eb;
}

.sci-link-subtle {
  display: inline-flex;
  align-items: center;
  font-size: 0.9rem;
  color: #2563eb;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s;
}

.sci-link-subtle:hover {
  color: #1d4ed8;
  text-decoration: underline;
}
</style>
